import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Translate as TranslateIcon } from '@mui/icons-material';
import { blogService, globalService, tourService } from '../../services/firebase';
import { Blog, Tour, TranslatableField } from '../../types';
import ImageUploadField from '../../components/Common/ImageUploadField';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { produce } from 'immer';

const getTranslatableString = (field: TranslatableField | string | undefined, lang: string): string => {
    if (!field) return '';
    if (typeof field === 'string') {
        return lang === 'tr' ? field : '';
    }
    return field[lang] || '';
};

const ManageBlogsPage: React.FC = () => {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [tours, setTours] = useState<Tour[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentBlog, setCurrentBlog] = useState<Partial<Blog> | null>(null);
    const [selectedLang, setSelectedLang] = useState('tr');
    const [isSaving, setIsSaving] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { t, i18n } = useTranslation();

    const fetchBlogsAndTours = useCallback(async () => {
        setLoading(true);
        try {
            const [blogsData, toursData] = await Promise.all([
                blogService.getBlogs(),
                tourService.getTours(),
            ]);
            setBlogs(blogsData);
            setTours(toursData);
        } catch (err) {
            setError(t('manageBlogs.errors.fetchError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchBlogsAndTours();
    }, [fetchBlogsAndTours]);

    const handleOpenDialog = (blog: Blog | null = null) => {
        if (blog) {
            setCurrentBlog(JSON.parse(JSON.stringify(blog)));
        } else {
            setCurrentBlog({
                title: { tr: '', en: '', ar: '' },
                content: { tr: '', en: '', ar: '' },
                excerpt: { tr: '', en: '', ar: '' },
                author: 'Hat Travel',
                category: { tr: '', en: '', ar: '' },
                tags: [],
                featuredImage: '',
                isPublished: false,
                seoTitle: { tr: '', en: '', ar: '' },
                seoDescription: { tr: '', en: '', ar: '' },
                seoKeywords: { tr: '', en: '', ar: '' },
                relatedTours: [],
                viewCount: 0,
            });
        }
        setSelectedLang('tr');
        setError(null);
        setSuccess(null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentBlog(null);
    };

    const handleInputChange = (field: keyof Blog, value: any) => {
        setCurrentBlog(produce(draft => {
            if (draft) {
                (draft as any)[field] = value;
            }
        }));
    };

    const handleTranslatableInputChange = (field: keyof Blog, value: string) => {
        setCurrentBlog(produce(draft => {
            if (draft) {
                let translatableField = (draft as any)[field];
                if (typeof translatableField !== 'object' || translatableField === null) {
                    translatableField = { tr: '', en: '', ar: '' };
                }
                translatableField[selectedLang] = value;
                (draft as any)[field] = translatableField;
            }
        }));
    };

    const handleSaveBlog = async () => {
        if (!currentBlog) return;
        setIsSaving(true);
        setError(null);
        
        try {
            const blogToSave: Partial<Blog> = produce(currentBlog, draft => {
                const content = getTranslatedData(draft.content, 'tr');
                draft.readTime = Math.max(1, Math.ceil((content?.split(' ').length || 0) / 200));
            });
            
            if (blogToSave.id) {
                await blogService.updateBlog(blogToSave.id, blogToSave as Omit<Blog, 'id'>);
                setSuccess(t('manageBlogs.success.update'));
            } else {
                await blogService.addBlog(blogToSave as Omit<Blog, 'id' | 'createdAt' | 'updatedAt' | 'viewCount'>);
                setSuccess(t('manageBlogs.success.create'));
            }
            fetchBlogsAndTours();
            handleCloseDialog();
        } catch (err: any) {
            setError(t('manageBlogs.errors.saveError', { message: err.message }));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteBlog = async (id: string) => {
        if (window.confirm(t('manageBlogs.confirmDelete'))) {
            try {
                await blogService.deleteBlog(id);
                setSuccess(t('manageBlogs.success.delete'));
                fetchBlogsAndTours();
            } catch (err: any) {
                setError(t('manageBlogs.errors.deleteError', { message: err.message }));
            }
        }
    };

    const handleTranslateAll = async () => {
        if (!currentBlog) return;
        setIsTranslating(true);
        setError(null);

        const translatableFields: (keyof Blog)[] = ['title', 'content', 'excerpt', 'category', 'seoTitle', 'seoDescription', 'seoKeywords'];
        
        try {
            const translations: Partial<Blog> = {};

            for (const field of translatableFields) {
                const sourceText = getTranslatedData((currentBlog as any)[field], 'tr');
                
                if (sourceText) {
                    const result = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en', 'ar'] });
                    const originalField = (currentBlog as any)[field];
                    (translations as any)[field] = {
                        ...(typeof originalField === 'object' ? originalField : { tr: sourceText }),
                        en: result.en,
                        ar: result.ar,
                    };
                }
            }
            
            setCurrentBlog(produce(currentBlog, draft => {
                if (draft) {
                    for (const field of translatableFields) {
                        if (translations[field]) {
                            (draft as any)[field] = (translations as any)[field];
                        }
                    }
                }
            }));

            setSuccess(t('manageBlogs.success.translate'));
        } catch (err: any) {
            setError(t('manageBlogs.errors.translateError', { message: err.message }));
        } finally {
            setIsTranslating(false);
        }
    };

    const columns: GridColDef<Blog>[] = [
        { field: 'title', headerName: t('manageBlogs.grid.title'), width: 300, valueGetter: (_, row) => getTranslatedData(row.title, i18n.language) },
        { field: 'category', headerName: t('manageBlogs.grid.category'), width: 150, valueGetter: (_, row) => getTranslatedData(row.category, i18n.language) },
        { field: 'author', headerName: t('manageBlogs.grid.author'), width: 150 },
        { field: 'viewCount', headerName: t('manageBlogs.grid.views'), width: 100 },
        { field: 'readTime', headerName: t('manageBlogs.grid.readTime'), width: 120, valueFormatter: (value) => `${value || 1} min` },
        { field: 'isPublished', headerName: t('manageBlogs.grid.status'), width: 120, type: 'boolean' },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('manageBlogs.grid.actions'),
            width: 120,
            getActions: (params: GridRowParams<Blog>) => [
                <GridActionsCellItem icon={<EditIcon />} label={t('edit')} onClick={() => handleOpenDialog(params.row)} />,
                <GridActionsCellItem icon={<DeleteIcon />} label={t('delete')} onClick={() => handleDeleteBlog(params.id as string)} />,
            ],
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">{t('manageBlogs.title')}</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        {t('manageBlogs.addBlog')}
                    </Button>
                </Box>

                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

                <Box sx={{ height: 650, width: '100%' }}>
                    <DataGrid rows={blogs} columns={columns} loading={loading} getRowId={(row) => row.id} />
                </Box>
            </Paper>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="lg">
                <DialogTitle>{currentBlog?.id ? t('manageBlogs.dialog.editTitle') : t('manageBlogs.dialog.createTitle')}</DialogTitle>
                <DialogContent>
                    {currentBlog && (
                        <Box>
                            <Tabs value={selectedLang} onChange={(_, newValue) => setSelectedLang(newValue)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Tab label="Türkçe" value="tr" />
                                <Tab label="English" value="en" />
                                <Tab label="العربية" value="ar" />
                            </Tabs>

                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, mt: 2 }}>
                                <TextField sx={{ gridColumn: 'span 12' }} label={t('manageBlogs.form.title')} value={getTranslatableString(currentBlog.title, selectedLang)} onChange={(e) => handleTranslatableInputChange('title', e.target.value)} />
                                <TextField sx={{ gridColumn: 'span 6' }} label={t('manageBlogs.form.author')} value={currentBlog.author || ''} onChange={(e) => handleInputChange('author', e.target.value)} />
                                <TextField sx={{ gridColumn: 'span 6' }} label={t('manageBlogs.form.category')} value={getTranslatableString(currentBlog.category, selectedLang)} onChange={(e) => handleTranslatableInputChange('category', e.target.value)} />

                                <Box sx={{ gridColumn: 'span 12' }}>
                                    <Typography sx={{ my: 1, fontWeight: '500' }}>{t('manageBlogs.form.content')}</Typography>
                                    <ReactQuill
                                        theme="snow"
                                        value={getTranslatableString(currentBlog.content, selectedLang)}
                                        onChange={(val) => handleTranslatableInputChange('content', val)}
                                        style={{ height: '250px', marginBottom: '50px' }}
                                    />
                                </Box>

                                <TextField sx={{ gridColumn: 'span 12', mt: '50px' }} label={t('manageBlogs.form.excerpt')} multiline rows={3} value={getTranslatableString(currentBlog.excerpt, selectedLang)} onChange={(e) => handleTranslatableInputChange('excerpt', e.target.value)} />
                                
                                <Autocomplete
                                    multiple
                                    sx={{ gridColumn: 'span 12' }}
                                    options={currentBlog.tags || []}
                                    value={currentBlog.tags || []}
                                    freeSolo
                                    onChange={(_, newValue) => handleInputChange('tags', newValue)}
                                    renderTags={(value: readonly string[], getTagProps) =>
                                        value.map((option: string, index: number) => (
                                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('manageBlogs.form.tags')} placeholder={t('manageBlogs.form.tagsPlaceholder')} />
                                    )}
                                />

                                <Box sx={{ gridColumn: 'span 12' }}>
                                    <ImageUploadField label={t('manageBlogs.form.featuredImage')} value={currentBlog.featuredImage || ''} onChange={(url) => handleInputChange('featuredImage', url)} storagePath="blogs" />
                                </Box>
                                
                                <Typography sx={{ gridColumn: 'span 12', mt: 2, fontWeight: 'bold' }}>{t('manageBlogs.form.seo')}</Typography>
                                <TextField sx={{ gridColumn: 'span 12' }} label={t('manageBlogs.form.seoTitle')} value={getTranslatableString(currentBlog.seoTitle, selectedLang)} onChange={(e) => handleTranslatableInputChange('seoTitle', e.target.value)} />
                                <TextField sx={{ gridColumn: 'span 12' }} label={t('manageBlogs.form.seoDescription')} multiline rows={2} value={getTranslatableString(currentBlog.seoDescription, selectedLang)} onChange={(e) => handleTranslatableInputChange('seoDescription', e.target.value)} />
                                <TextField sx={{ gridColumn: 'span 12' }} label={t('manageBlogs.form.seoKeywords')} value={getTranslatableString(currentBlog.seoKeywords, selectedLang)} onChange={(e) => handleTranslatableInputChange('seoKeywords', e.target.value)} helperText={t('manageBlogs.form.seoKeywordsHelper')} />
                                
                                <Autocomplete
                                    multiple
                                    sx={{ gridColumn: 'span 12' }}
                                    options={tours}
                                    value={tours.filter(t => currentBlog.relatedTours?.some(rt => rt.id === t.id))}
                                    getOptionLabel={(option) => getTranslatedData(option.name, i18n.language)}
                                    onChange={(_, newValue) => {
                                        const relatedTours = newValue.map(t => ({ id: t.id, name: getTranslatedData(t.name, 'tr'), destination: getTranslatedData(t.destination, 'tr'), duration: `${t.duration} gün`, price: t.price }));
                                        handleInputChange('relatedTours', relatedTours);
                                    }}
                                    renderInput={(params) => <TextField {...params} label={t('manageBlogs.form.relatedTours')} />}
                                />

                                <FormControlLabel sx={{ gridColumn: 'span 12' }} control={<Switch checked={currentBlog.isPublished || false} onChange={(e) => handleInputChange('isPublished', e.target.checked)} />} label={t('manageBlogs.form.published')} />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Tooltip title={t('manageBlogs.tooltips.translate')}>
                        <IconButton onClick={handleTranslateAll} disabled={isTranslating || selectedLang !== 'tr'}>
                            {isTranslating ? <CircularProgress size={24} /> : <TranslateIcon />}
                        </IconButton>
                    </Tooltip>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
                    <Button onClick={handleSaveBlog} variant="contained" disabled={isSaving}>
                        {isSaving ? t('saving') : t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageBlogsPage; 